import React, { memo, useCallback, useEffect, useMemo, useState } from 'react';

import RadioButtonCheckedIcon from '@mui/icons-material/RadioButtonChecked';
import RadioButtonUncheckedIcon from '@mui/icons-material/RadioButtonUnchecked';
import { Box, Button, List, ListItem, Typography } from '@mui/material';
import { Spinner } from '@provider-portal/common/components/Spinner/index';

import { useMutedCallback } from 'hooks/useMutedCallback';

import { Promotion } from '../../types';
import { PromotionFooter } from '../PromotionFooter/PromotionFooter';
import { isNullable } from '../utils';

export interface PromotionsListFCProps {
  strings: {
    title: string;
    buttons: {
      back: string;
      continue: string;
    };
  };
  initialValue?: Promotion;
  promotionItems: Promotion[] | Error;
  isItemsLoading: boolean;
  onClickBack: () => void;
  onClickNext: (promotion: Promotion) => void;
}

interface PromotionListProps {
  promotionsList: Promotion[];
  currentSelectedPromotion: string | undefined;
  onPickedPromotion: (promotionName: string, isPicked: boolean) => void;
}

interface SelectionPromotionProps {
  promotionName: string;
  promotionDescription: string;
  promotionTrait: string;
  isPicked: boolean;
  onPick: (name: string, isPicked: boolean) => void;
}

const PromotionItemIcon = React.memo(({ isPicked }: { isPicked: boolean }) => {
  return isPicked ? (
    <RadioButtonCheckedIcon fontSize="small" color="primary" />
  ) : (
    <RadioButtonUncheckedIcon fontSize="small" color="disabled" />
  );
});

const SelectionPromotion = React.forwardRef<typeof ListItem, SelectionPromotionProps>(
  (props, _ref) => {
    const { promotionName, promotionDescription, promotionTrait, isPicked, onPick } = props;

    const pickPromotionHandler = useCallback(
      () => onPick(promotionTrait, !isPicked),
      [promotionTrait, isPicked, onPick],
    );

    return (
      <Box
        display="flex"
        alignItems="center"
        mr={2}
        mb={3}
        py={2}
        px={3}
        onClick={pickPromotionHandler}
        sx={{
          borderRadius: '12px',
          border: 'solid 1px',
          borderColor: isPicked ? 'colors.green500' : 'colors.neutral300',
        }}
      >
        <PromotionItemIcon isPicked={isPicked} />
        <Box ml={2} flexDirection="row">
          <Box>
            <Typography variant="subtitleM">{promotionName}</Typography>
          </Box>
          <Box>
            <Typography variant="bodyM">{promotionDescription}</Typography>
          </Box>
        </Box>
      </Box>
    );
  },
);

const ListPromotions: React.FC<PromotionListProps> = memo(
  ({ promotionsList, currentSelectedPromotion, onPickedPromotion }) => {
    const onPickHandler = useCallback(
      (promotionName, isPicked) => {
        onPickedPromotion(promotionName, isPicked);
      },
      [onPickedPromotion],
    );

    const generatePromotionsList = useMemo(() => {
      return promotionsList.map((row) => {
        const isPicked = row.trait === currentSelectedPromotion;
        return (
          <SelectionPromotion
            key={row.trait}
            promotionName={row.title}
            promotionDescription={row.description}
            promotionTrait={row.trait}
            isPicked={isPicked}
            onPick={onPickHandler}
          />
        );
      });
    }, [promotionsList, currentSelectedPromotion, onPickHandler]);
    return (
      <Box>
        <List>{generatePromotionsList}</List>
      </Box>
    );
  },
);

export const PromotionsListFC = memo(
  ({
    strings,
    initialValue,
    promotionItems,
    isItemsLoading,
    onClickBack,
    onClickNext,
  }: PromotionsListFCProps) => {
    const [promotionPicked, setSelectedPromotion] = useState<Promotion | null>(null);

    useEffect(() => {
      setSelectedPromotion(initialValue ?? null);
    }, [initialValue]);

    const onPickPromotionHandler = useCallback(
      (newId: string, onPickPromotionClick: boolean) => {
        setSelectedPromotion((selectedPromotion) =>
          onPickPromotionClick
            ? (Array.isArray(promotionItems) &&
                promotionItems.find(({ trait }) => trait === newId)) ||
              selectedPromotion
            : selectedPromotion,
        );
      },
      [promotionItems],
    );

    const content = useMemo(() => {
      if (isItemsLoading) {
        return <Spinner />;
      }
      if (Array.isArray(promotionItems)) {
        return (
          <ListPromotions
            promotionsList={promotionItems}
            currentSelectedPromotion={promotionPicked?.trait}
            onPickedPromotion={onPickPromotionHandler}
          />
        );
      }
      return null;
    }, [isItemsLoading, onPickPromotionHandler, promotionItems, promotionPicked?.trait]);

    const onClickContinueHandler = useCallback(() => {
      if (promotionPicked) {
        onClickNext(promotionPicked);
      }
    }, [onClickNext, promotionPicked]);
    const onClickBackHandler = useMutedCallback(onClickBack);

    const isContinueDisabled = useMemo(() => isNullable(promotionPicked), [promotionPicked]);

    return (
      <Box>
        <Box mb={3}>
          <Typography variant="h6">{strings.title}</Typography>
        </Box>
        {content}
        <PromotionFooter hideTacText>
          <Box mr={1}>
            <Button variant="contained" color="secondary" onClick={onClickBackHandler}>
              {strings.buttons.back}
            </Button>
          </Box>
          <Box>
            <Button
              variant="contained"
              color="primary"
              onClick={onClickContinueHandler}
              disabled={isContinueDisabled}
            >
              {strings.buttons.continue}
            </Button>
          </Box>
        </PromotionFooter>
      </Box>
    );
  },
);
